import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaSearch, FaTimes, FaBirthdayCake, FaMicrophone } from 'react-icons/fa';
import { TbTruckDelivery } from 'react-icons/tb';
import logo from '@assets/pcakelogo.png';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase } from '@services/firebase';
import { useEnquiryBadge } from '@hooks/useEnquiryBadge';
import '../index.css';

const searchPlaceholderStyle = `
  .topbar-search::placeholder { color: #444; font-size: 11px; }
  html[data-theme="dark"] .topbar-search-wrapper { background: #cdd1d8 !important; }
  html[data-theme="dark"] .topbar-search::placeholder { color: #333 !important; font-size: 11px; }
`;

const { auth } = getFirebase();

const TopBar = ({ onSearchChange, userRole }) => {
  const [user] = useAuthState(auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const location = useLocation();
  const hasEnquiryUpdate = useEnquiryBadge(user, userRole);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearchChange(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearchChange('');
  };

  const supportsSpeech = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      onSearchChange(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  const isHomePage = location.pathname === '/';
  const isSharedPage = location.pathname === '/shared';

  // Determine the icon and navigation based on user role
  const isMover = userRole === 'Mover';
  const isAdmin = userRole === 'Admin';
  const isBuyer = userRole === 'Buyer';
  const isSuper = userRole === 'Super';
  const isUser = userRole === 'User';
  const showCartIcon = isMover || isAdmin || isBuyer || isSuper;
  const IconComponent = isMover ? TbTruckDelivery : FaBirthdayCake;
  const navigationPath = isMover ? '/delorder' : isBuyer ? '/enquiry' :'/order';

  return (
    <div className="top-bar" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <style>{searchPlaceholderStyle}</style>
      {/* Conditionally render logo with Link for non-Movers, plain div for Movers */}
      {isMover || isSharedPage ? (
        <div>
          <img src={logo} alt="PCake Logo" className="logo" />
        </div>
      ) : (
        <Link to="/">
          <img src={logo} alt="PCake Logo" className="logo" />
        </Link>
      )}
      {!user ? null : isHomePage && (
        <div
          className="topbar-search-wrapper"
          style={{
            flex: 1,
            margin: '0 12px',
            background: '#f0f2f5',
            borderRadius: '8px',
            padding: '0 6px 0 10px',
            display: 'flex',
            alignItems: 'center',
            height: '35px',
            position: 'relative',
          }}
        >
          <input
            type="text"
            className="topbar-search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by Theme, Flavour, Event, Shape ..."
            style={{
              flex: 1,
              padding: '0 4px 0 0',
              border: 'none',
              background: 'transparent',
              fontSize: '14px',
              outline: 'none',
              color: '#333',
              minWidth: 0,
            }}
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#555',
                fontSize: '14px',
                padding: '4px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <FaTimes />
            </button>
          )}
          {supportsSpeech && (
            <button
              onClick={handleVoiceSearch}
              title={isListening ? 'Listening...' : 'Voice search'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isListening ? '#e53935' : '#555',
                fontSize: '14px',
                padding: '4px',
                lineHeight: 1,
                flexShrink: 0,
                transition: 'color 0.2s',
              }}
            >
              <FaMicrophone />
            </button>
          )}
        </div>
      )}
      {showCartIcon && (
        <NavLink to={navigationPath} className={({ isActive }) => isActive ? 'cart-icon-link cart-icon-link--active' : 'cart-icon-link'}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <IconComponent className="cart-icon" />
            {isBuyer && hasEnquiryUpdate && (
              <span
                style={{
                  position: 'absolute',
                  bottom: '-4px',
                  right: '-4px',
                  width: '9px',
                  height: '9px',
                  backgroundColor: '#4caf50',
                  borderRadius: '50%',
                  border: '1.5px solid #fff',
                  display: 'block',
                }}
              />
            )}
          </div>
        </NavLink>
      )}
    </div>
  );
};

export default TopBar;